import { computed, nextTick, type Ref } from "vue";
import type { FormContext } from "vee-validate";
import type {
  DeliverySelectValue,
  DisplayDaysSettingValue,
  CapSelectValue,
  AreaSelectValue,
} from "../types";
import {
  DeliveryTimeCommonInput,
} from "@/generated/graphql";

interface UseDeliverySettingTimeFormParams {
  processFlag: Ref<number>;
  mode: Ref<string>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  timeFormRef: Ref<FormContext | null>;
  deliveryTimeCommonInput: DeliveryTimeCommonInput;
  areaEditSelectValues: Ref<AreaSelectValue>;
  deliverySelectValues: Ref<DeliverySelectValue>;
  selectTimeCapSelectValues: Ref<CapSelectValue>;
  displayDaysSettingValues: Ref<DisplayDaysSettingValue>;
  wordList: Record<string, string>;
}

/**
 * フォーム入力状態と変換処理を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useDeliverySettingTimeForm = ({
  processFlag,
  mode,
  errdialog,
  errMessage,
  timeFormRef,
  deliveryTimeCommonInput,
  areaEditSelectValues,
  deliverySelectValues,
  selectTimeCapSelectValues,
  displayDaysSettingValues,
  wordList,
}: UseDeliverySettingTimeFormParams) => {
  const fieldName = (key: string): string => {
    return wordList[key] || key;
  };

  const isValidTimeValue = (value: unknown): boolean => {
    if (value === null || value === undefined) {
      return false;
    }
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value));
  };

  const isValidNumberValue = (value: unknown): boolean => {
    if (value === null || value === undefined || value === "") {
      return false;
    }
    return Number.isFinite(Number(value));
  };

  const isTimeFormValid = computed((): boolean => {
    if (processFlag.value === 0) {
      if (!areaEditSelectValues.value?.area_code) {
        return false;
      }
      if (
        deliverySelectValues.value?.delivery_id === null ||
        deliverySelectValues.value?.delivery_id === undefined
      ) {
        return false;
      }
    }

    if (!isValidTimeValue(deliveryTimeCommonInput.delivery_start_time)) {
      return false;
    }
    if (!isValidTimeValue(deliveryTimeCommonInput.delivery_end_time)) {
      return false;
    }
    if (
      deliveryTimeCommonInput.delivery_start_time >
      deliveryTimeCommonInput.delivery_end_time
    ) {
      return false;
    }

    if (selectTimeCapSelectValues.value.index === 0) {
      return isValidNumberValue(
        deliveryTimeCommonInput.max_deliverable_unit
      );
    }

    return [
      deliveryTimeCommonInput.delivery_cap_mon,
      deliveryTimeCommonInput.delivery_cap_tue,
      deliveryTimeCommonInput.delivery_cap_wed,
      deliveryTimeCommonInput.delivery_cap_thu,
      deliveryTimeCommonInput.delivery_cap_fri,
      deliveryTimeCommonInput.delivery_cap_sat,
      deliveryTimeCommonInput.delivery_cap_sun,
    ].every((value) => isValidNumberValue(value));
  });

  /**
   * 配達開始時間変更時 @change
   */
  const onChangeDeliveryStartTime = (): void => {
    if (
      deliveryTimeCommonInput.delivery_start_time !== "" &&
      deliveryTimeCommonInput.delivery_end_time !== "" &&
      deliveryTimeCommonInput.delivery_start_time >
        deliveryTimeCommonInput.delivery_end_time
    ) {
      errdialog.value = true;
      errMessage.value =
        "配達時間の開始時間は、終了時間以前の時刻を入力してください。";
      deliveryTimeCommonInput.delivery_start_time = "";
      const form = timeFormRef.value;
      if (form) {
        form.setFieldValue(fieldName("lbl_delivery_start_time"), "", false);
      }
    }
  };

  /**
   * 配達終了時間変更時 @change
   */
  const onChangeDeliveryEndTime = (): void => {
    if (
      deliveryTimeCommonInput.delivery_start_time !== "" &&
      deliveryTimeCommonInput.delivery_end_time !== "" &&
      deliveryTimeCommonInput.delivery_start_time >
        deliveryTimeCommonInput.delivery_end_time
    ) {
      errdialog.value = true;
      errMessage.value =
        "配達時間の終了時間は、開始時間以降の時刻を入力してください。";
      deliveryTimeCommonInput.delivery_end_time = "";
      const form = timeFormRef.value;
      if (form) {
        form.setFieldValue(fieldName("lbl_delivery_end_time"), "", false);
      }
    }
  };

  // 編集画面初期表示時に vee-validate の内部値を同期する。
  const syncTimeFormValues = async (): Promise<void> => {
    if (mode.value !== "time" || processFlag.value === 2) {
      return;
    }
    await nextTick();
    const form = timeFormRef.value;
    if (!form) {
      return;
    }

    if (processFlag.value === 0) {
      form.setFieldValue(
        fieldName("lbl_area_name"),
        areaEditSelectValues.value,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_id"),
        deliverySelectValues.value,
        false
      );
    }

    form.setFieldValue(
      fieldName("lbl_delivery_start_time"),
      deliveryTimeCommonInput.delivery_start_time,
      false
    );
    form.setFieldValue(
      fieldName("lbl_delivery_end_time"),
      deliveryTimeCommonInput.delivery_end_time,
      false
    );
    form.setFieldValue(
      fieldName("lbl_delivery_cap_setting"),
      selectTimeCapSelectValues.value.index,
      false
    );

    if (selectTimeCapSelectValues.value.index === 0) {
      form.setFieldValue(
        fieldName("lbl_max_deliverable_unit"),
        deliveryTimeCommonInput.max_deliverable_unit,
        false
      );
    } else {
      form.setFieldValue(
        fieldName("lbl_delivery_cap_mon"),
        deliveryTimeCommonInput.delivery_cap_mon,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_tue"),
        deliveryTimeCommonInput.delivery_cap_tue,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_wed"),
        deliveryTimeCommonInput.delivery_cap_wed,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_thu"),
        deliveryTimeCommonInput.delivery_cap_thu,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_fri"),
        deliveryTimeCommonInput.delivery_cap_fri,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_sat"),
        deliveryTimeCommonInput.delivery_cap_sat,
        false
      );
      form.setFieldValue(
        fieldName("lbl_delivery_cap_sun"),
        deliveryTimeCommonInput.delivery_cap_sun,
        false
      );
    }

    form.setFieldValue(
      fieldName("lbl_display_setting"),
      displayDaysSettingValues.value,
      false
    );
  };

  return {
    fieldName,
    isValidTimeValue,
    isValidNumberValue,
    isTimeFormValid,
    onChangeDeliveryStartTime,
    onChangeDeliveryEndTime,
    syncTimeFormValues,
  };
};
